import React, { useRef, useState } from 'react';

import { ReactComponent as IconDropDownArrow } from '../../assets/svgs/icon-dropdown-arrow.svg';
import { useOutsideClick } from '../../hooks';
import usePrevious from '../../hooks/usePrevious';

interface Option {
  text: string;
  value: string;
}

interface Props {
  activeIndex: number;
  dataTestID?: string;
  onChange: (value: string) => void;
  onClear?: () => void;
  options: Option[];
  placeholder: string;
  setActiveIndex: (activeItemIndex: number) => void;
}

export const SingleSelectDropDown: React.FC<Props> = ({
  activeIndex,
  dataTestID = '',
  onChange = () => {},
  onClear = () => {},
  options,
  placeholder,
  setActiveIndex,
}) => {
  const [showDropdown, setShowDropdown] = useState(false);
  const previousActiveIndex = usePrevious(activeIndex);

  const listRef = useRef(null);

  useOutsideClick(listRef, () => {
    if (activeIndex !== -1 && showDropdown && previousActiveIndex !== activeIndex) {
      onChange(options[activeIndex].value);
    }
    setShowDropdown(false);
  });

  const hasActiveIndex = activeIndex !== -1;

  return (
    <div className="z-30 relative h-10 my-3 mr-2" ref={listRef} data-testid={dataTestID}>
      {showDropdown && (
        <ul className="border shadow-lg rounded absolute bg-white border-gray-300 mx-1 top-12 w-48 z-20">
          {options.map((item, i) => (
            <li
              key={item.text + i}
              onClick={() => {
                setActiveIndex(i);
              }}
              data-testid={dataTestID + 'Option' + item.text}
              className={`${
                activeIndex === i ? 'text-blue-200 bg-blue-100' : 'text-gray-600 hover:bg-gray-100'
              } flex flex-row justify-start items-center cursor-pointer font-medium p-3 text-left`}
            >
              <div
                className={`${
                  activeIndex === i ? 'border-2 border-blue-200' : 'border-2 border-gray-300'
                } mr-3 rounded-full p-.5`}
              >
                <div className={`${activeIndex === i ? 'bg-blue-200' : ''} w-3 h-3 rounded-full`} />
              </div>
              {item.text}
            </li>
          ))}
          <li className="flex flex-row justify-between p-3 border-t border-gray-300">
            <button
              onClick={() => {
                setShowDropdown(false);
                setActiveIndex(-1);
                onChange(options[0].value);
                onClear();
              }}
              data-testid={dataTestID + 'Clear'}
              className="text-gray-600 font-medium"
            >
              Clear
            </button>
            <button
              onClick={() => {
                setShowDropdown(false);
                if (hasActiveIndex) {
                  onChange(options[activeIndex].value);
                }
              }}
              data-testid={dataTestID + 'Save'}
              className="bg-blue-200 text-white font-medium py-2 px-4 rounded"
            >
              Save
            </button>
          </li>
        </ul>
      )}
      <button
        onClick={() => setShowDropdown(!showDropdown)}
        style={{ width: 'max-content' }}
        className={`
        ${hasActiveIndex && !showDropdown ? 'border-blue-200 bg-blue-100 text-blue-200' : ''}
        border rounded text-gray-800 placeholder-gray-600 font-medium flex flex-row justify-between items-center mx-1 px-2 border-gray-300 p-1 w-48 h-full focus:outline-none`}
      >
        {placeholder}
        <IconDropDownArrow
          className={`${
            hasActiveIndex && !showDropdown
              ? 'text-blue-200 fill-current stroke-current'
              : 'text-gray-600 fill-current stroke-current'
          } ml-6 fill-current h-2 w-2`}
        />
      </button>
    </div>
  );
};
